import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '../../stores/uiStore';
import { useCanRoute } from '../../lib/rbac';
import {
  LayoutDashboard, Package, ShoppingCart, MessageCircle, Mail,
  Warehouse as WarehouseIcon, Store, Rocket, DollarSign,
  Settings, ChevronLeft, X, Star, TrendingDown, Activity, Gauge, Bot
} from 'lucide-react';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'nav.dashboard' },
  { to: '/orders', icon: Package, label: 'nav.orders' },
  { to: '/questions', icon: MessageCircle, label: 'nav.questions' },
  { to: '/messages', icon: Mail, label: 'nav.messages' },
  { to: '/assistant', icon: Bot, label: 'nav.assistant' },
  { to: '/purchases', icon: ShoppingCart, label: 'nav.purchases' },
  { to: '/warehouse', icon: WarehouseIcon, label: 'nav.warehouse' },
  { to: '/catalog', icon: Store, label: 'nav.catalog' },
  { to: '/publisher', icon: Rocket, label: 'nav.publisher' },
  { to: '/finance', icon: DollarSign, label: 'nav.finance' },
  { to: '/admin/reviews', icon: Star, label: 'nav.reviews' },
  { to: '/admin/pricing-losses', icon: TrendingDown, label: 'nav.pricingLosses' },
  { to: '/admin/health', icon: Activity, label: 'nav.systemHealth' },
  { to: '/admin/price-monitor', icon: Gauge, label: 'nav.priceMonitorRuns' },
  { type: 'divider' as const, label: '' },
  { to: '/settings', icon: Settings, label: 'nav.settings' },
] as const;

export function Sidebar() {
  const { t } = useTranslation();
  const { sidebarCollapsed, sidebarMobileOpen, toggleSidebar, setSidebarMobileOpen } = useUIStore();
  const canGo = useCanRoute();

  // Solo los destinos permitidos; y un separador sobrevive solo si queda algo a cada lado.
  const allowed = navItems.filter((item) => !('to' in item) || canGo(item.to));
  const items = allowed.filter(
    (item, i) =>
      !('type' in item && item.type === 'divider') ||
      (allowed.slice(0, i).some((p) => 'to' in p) && allowed.slice(i + 1).some((n) => 'to' in n)),
  );

  return (
    <>
      {sidebarMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarMobileOpen(false)}
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 h-full z-50
          bg-[var(--color-sidebar-bg)] border-r border-[var(--color-border)]
          transition-all duration-300 flex flex-col
          ${sidebarCollapsed ? 'w-[var(--sidebar-collapsed-width)]' : 'w-[var(--sidebar-width)]'}
          ${sidebarMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="h-[var(--topbar-height)] flex items-center px-4 border-b border-[var(--color-border)] shrink-0">
          {!sidebarCollapsed ? (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-[var(--color-brand)] flex items-center justify-center">
                <span className="text-xs font-bold text-[#333]">F5</span>
              </div>
              <span className="text-sm font-semibold text-[var(--color-text)]">CBT Command Center</span>
            </div>
          ) : (
            <div className="w-7 h-7 rounded-md bg-[var(--color-brand)] flex items-center justify-center mx-auto">
              <span className="text-xs font-bold text-[#333]">F5</span>
            </div>
          )}
          <button
            onClick={() => setSidebarMobileOpen(false)}
            className="lg:hidden ml-auto p-1 hover:bg-[var(--color-sidebar-hover)] rounded"
          >
            <X size={18} className="text-[var(--color-sidebar-text)]" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {items.map((item, i) => {
            if ('type' in item && item.type === 'divider') {
              return <div key={i} className="my-2 mx-3 border-t border-[var(--color-border)]" />;
            }
            if (!('to' in item)) return null;
            const Icon = item.icon;

            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={() => setSidebarMobileOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5
                  transition-colors duration-150
                  ${isActive
                    ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)] font-medium border-l-[3px] border-[var(--color-primary)]'
                    : 'text-[var(--color-sidebar-text)] hover:bg-[var(--color-sidebar-hover)] hover:text-[var(--color-text)]'
                  }
                  ${sidebarCollapsed ? 'justify-center' : ''}
                `}
                title={sidebarCollapsed ? t(item.label) : undefined}
              >
                <Icon size={20} className="shrink-0" />
                {!sidebarCollapsed && (
                  <span className="text-sm truncate">{t(item.label)}</span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Collapse toggle */}
        <button
          onClick={toggleSidebar}
          className="hidden lg:flex items-center justify-center h-10 border-t border-[var(--color-border)] hover:bg-[var(--color-sidebar-hover)] transition-colors"
        >
          <ChevronLeft
            size={18}
            className={`text-[var(--color-sidebar-text)] transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`}
          />
        </button>
      </aside>
    </>
  );
}
